/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '@ion/applications/src/store/helpers';
import {PageState} from '@ion/applications/src/types/pages';
import {Crashboxx} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {Map} from '@ion/features/src/maps/hooks/useMapOperations';
import {BasemapTypes} from '@ion/features/src/maps/types/types';
import {PageInitializeAction} from '@/types/redux';

// defines the page state...
export interface CrashBoxxState extends PageState {
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    filterModel: FilterModel | {}
  },
  crashboxx: {
    error: ServiceError;
    loading: boolean;
  }
  view: Crashboxx;
  map: Map;
};

// reducer name...
export const NAME = 'crashboxx';

// grid related actions types...
export const INITIALIZE_CRASHBOXX            = 'INITIALIZE_CRASHBOXX';
export const SET_GRID_COLUMNS                = 'SET_CRASHBOXX_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS             = 'UPDATE_CRASHBOXX_GRID_COLUMNS';
export const SET_CRASHBOXX_GRID_FILTER_MODEL = 'SET_CRASHBOXX_GRID_FILTER_MODEL';

// map
// View map
export const SET_MAP_INITIALIZED = 'SET_CRASH_MAP_INITIALIZED';
export const SET_MAP_BASEMAP     = 'SET_CRASH_MAP_BASEMAP';
export const SET_MAP_EXTENT      = 'SET_CRASH_MAP_EXTENT';
export const SET_MAP_ZOOM        = 'SET_CRASH_MAP_ZOOM';

// view event actions...
export const VIEW_CRASH_EVENTS = 'VIEW_CRASH_EVENTS';

// Invoked by IonGrid when the grid filter model changes.
export const setFilterModel = (model: FilterModel) => ({
  type : SET_CRASHBOXX_GRID_FILTER_MODEL, model
});

// invokes when the page mount initially...
export const initializeCrashBoxx = (): PageInitializeAction => {
  return {type: INITIALIZE_CRASHBOXX};
};

export const viewCrashEvents = (event: Crashboxx) => ({type: VIEW_CRASH_EVENTS, event});

export const setMapInitialized = () => ({
  type : SET_MAP_INITIALIZED
});

export const setMapBasemap = (basemap: any) => ({
  type : SET_MAP_BASEMAP, basemap
});

export const setMapExtent = (extent: any) => ({
  type : SET_MAP_EXTENT, extent
});

export const setMapZoom = (zoom: number) => ({
  type : SET_MAP_ZOOM, zoom
});

// defines the initial state for the reducer ...
export const initialState: CrashBoxxState = {
  isInitialized : false,
  crashboxx     : {
    loading : false,
    error   : null
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    filterModel         : {}
  },
  view : null,
  map  : {
    initialized : false,
    basemap     : BasemapTypes.grayVector,
    extent      : null,
    center      : null,
    zoom        : 6
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_CRASHBOXX] : (draft: CrashBoxxState) => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(NAME, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [SET_GRID_COLUMNS] : ({grid, crashboxx}: CrashBoxxState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : crashboxx.loading,
      preference : NAME
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: CrashBoxxState, {columnState}) => {
    onUpdateGridColumns({grid, preference: NAME}, columnState);
  },
  [VIEW_CRASH_EVENTS] : (draft: CrashBoxxState, {event}) => {
    draft.view = event;
  },
  [SET_MAP_INITIALIZED] : (draft: CrashBoxxState) => {
    draft.map.initialized = true;
  },
  [SET_MAP_BASEMAP] : (draft: CrashBoxxState, {basemap}) => {
    draft.map.basemap = basemap;
  },
  [SET_MAP_EXTENT] : (draft: CrashBoxxState, {extent}) => {
    draft.map.extent = extent;
  },
  [SET_MAP_ZOOM] : (draft: CrashBoxxState, {zoom}) => {
    draft.map.zoom = zoom;
  },
  [SET_CRASHBOXX_GRID_FILTER_MODEL] : ({grid}: CrashBoxxState, {model}) => {
    grid.filterModel = model;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: CrashBoxxState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
